import { useQuery } from "@apollo/client";
import { Card, InputGroup, Tag, Tree, TreeNodeInfo } from "@blueprintjs/core";
import _, { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Article, ArticleSource, Distributor } from "../../api/schema";
import {
  FlexBox,
  FlexContainer,
  FlexRowStretch,
} from "../../components/flex/Flex";
import { ViewHeader } from "../../components/form/Header";
import { Spinner } from "../../components/Spinner";
import { UserLanguage } from "../../lang/lang";
import {
  ArticleCategoryPrettyRender,
  GroupPrettyRender,
  LocationPrettyRender,
} from "../../utils/PrettyRenderer";
import { FootprintTypeIntent } from "../Footprint/ListView";
import { ArticleSourceViewQuery, ArticleSourceViewResponse } from "./graphql";

type NodePath = number[];

type TreeAction =
  | {
      type: "SET_IS_EXPANDED";
      payload: { path: NodePath; isExpanded: boolean };
    }
  | { type: "DESELECT_ALL" }
  | {
      type: "SET_IS_SELECTED";
      payload: { path: NodePath; isSelected: boolean };
    };

function forEachNode(
  nodes: TreeNodeInfo[] | undefined,
  callback: (node: TreeNodeInfo) => void
) {
  if (nodes === undefined) {
    return;
  }

  for (const node of nodes) {
    callback(node);
    forEachNode(node.childNodes, callback);
  }
}

function forNodeAtPath(
  nodes: TreeNodeInfo[],
  path: NodePath,
  callback: (node: TreeNodeInfo) => void
) {
  callback(Tree.nodeFromPath(path, nodes));
}

function treeExampleReducer(state: TreeNodeInfo[], action: TreeAction) {
  switch (action.type) {
    case "DESELECT_ALL":
      const newState1 = cloneDeep(state);
      forEachNode(newState1, (node) => (node.isSelected = false));
      return newState1;
    case "SET_IS_EXPANDED":
      const newState2 = cloneDeep(state);
      forNodeAtPath(
        newState2,
        action.payload.path,
        (node) => (node.isExpanded = action.payload.isExpanded)
      );
      return newState2;
    case "SET_IS_SELECTED":
      const newState3 = cloneDeep(state);
      forNodeAtPath(
        newState3,
        action.payload.path,
        (node) => (node.isSelected = action.payload.isSelected)
      );
      return newState3;
    default:
      return state;
  }
}

interface ArticleSourceListViewState {
  data: ArticleSourceViewResponse | null;
}

interface PSDComplex {
  distributor: Distributor;
  sources: ArticleSource[];
}

type KeydComplex = { [key: string]: PSDComplex };

const buildPartSourceNode = (
  partSources: ArticleSource[]
): TreeNodeInfo<any>[] => {
  const dist: KeydComplex = {};

  _.forEach(partSources, (partSource) => {
    if (!_.get(dist, partSource.distributor.id)) {
      _.set(dist, partSource.distributor.id, {
        distributor: partSource.distributor,
        sources: [],
      });
    }
    _.get(dist, partSource.distributor.id).sources.push(partSource);
  });

  return _.map<KeydComplex, TreeNodeInfo<any>>(dist, (bundle) => ({
    id: bundle.distributor.id,
    label: bundle.distributor.name,
    hasCaret: true,
    icon: "truck",
    nodeData: {
      ...bundle.distributor,
      partSources: bundle.sources,
    },
    childNodes: _.map<ArticleSource, TreeNodeInfo<any>>(
      bundle.sources,
      (source) => ({
        id: source.id,
        label: source.order_number,
        nodeData: source,
        icon: "dot",
      })
    ),
  }));
};

const buildPartNode = (part: Article) => {
  let data: TreeNodeInfo<Article> = {
    id: part.id,
    label: part.name,
    hasCaret: true,
    childNodes: buildPartSourceNode(part.sources),
    nodeData: part,
    icon: "cube",
  };

  return data;
};

export interface CreatePartSourceState {
  orderNumber: string;
  distributor: Distributor;
  article: Article;
  url: string | null;
}

interface SelectState {
  article: Article | null;
  distributor: Distributor | null;
  articleSource: ArticleSource | null;
}

const ArticleSourceDetail = (props: { source: ArticleSource }) => {
  const { source } = props;
  let orderNode;
  if (source.url) {
    orderNode = (
      <a href={source.url} target="_blank" rel="noreferrer">
        {source.order_number}
      </a>
    );
  } else if (source.distributor.search_url) {
    orderNode = (
      <a
        href={source.distributor.search_url.replace("%s", source.order_number)}
        target="_blank"
        rel="noreferrer"
      >
        {source.order_number}
      </a>
    );
  } else {
    orderNode = source.order_number;
  }
  return (
    <div>
      <ViewHeader title={source.id.toString()} />
      <div>
        {UserLanguage().data.articleSource.order_number}: {orderNode}
      </div>
      <div>
        {UserLanguage().data.articleSource.moq}: {source.moq}
      </div>
      {source.quantity_scale.length > 0 ? (
        <div>
          {UserLanguage().data.quantity_scale.label}:
          {_.map(source.quantity_scale, (qs, index) => (
            <div key={source.id + "_qs_" + index}>
              {qs.quantity}: {qs.price.toFixed(4)}€
            </div>
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const PartDetail = (props: { part: Article }) => {
  const { part } = props;

  const datasheet =
    part.datasheet_url !== null ? (
      <>
        {UserLanguage().data.article.datasheet_url}:{" "}
        <a rel="noreferrer" target="_blank" href={part.datasheet_url}>
          {UserLanguage().actions.followLink}
        </a>
      </>
    ) : (
      <></>
    );

  let picture = <></>;
  if (part.picture) {
    if (part.picture.startsWith("http")) {
      picture = <img src={part.picture} width={200} height={200} alt="" />;
    } else if (part.picture.startsWith("data:image")) {
      picture = <img src={part.picture} width={200} height={200} alt="" />;
    }
  }

  return (
    <div>
      <h3>
        {UserLanguage().data.id}: {part.id}
      </h3>
      <h3>
        {UserLanguage().data.name}: {part.name}
      </h3>
      {part.footprint ? (
        <h3>
          {UserLanguage().data.footprint.label}: [{part.footprint.id}]{" "}
          {part.footprint.name} -{" "}
          <Tag intent={FootprintTypeIntent[part.footprint.type]} large>
            {part.footprint.type}
          </Tag>
        </h3>
      ) : null}
      <h3>
        {UserLanguage().data.location.label}:{" "}
        {LocationPrettyRender(part.location)}
      </h3>
      <h3>
        {UserLanguage().data.product_group.label}:{" "}
        {GroupPrettyRender(part.product_group)}
      </h3>
      <h3>
        {UserLanguage().data.articleCategory.label}:{" "}
        {ArticleCategoryPrettyRender(part.article_category)}
      </h3>
      <h3>{datasheet}</h3>
      <h3>{picture}</h3>
    </div>
  );
};

const DistributorDetail = (props: { distributor: Distributor }) => {
  const { distributor } = props;
  return (
    <div>
      <h3>
        {distributor.id} / {distributor.name}
      </h3>
    </div>
  );
};

export const ArticleSourceTree = (props: {
  data: ArticleSourceViewResponse;
  refetch: () => void;
}) => {
  const [selectState, setSelectState] = useState<SelectState>({
    distributor: null,
    article: null,
    articleSource: null,
  });

  const [searchQuery, setSearchQuery] = useState("");

  const treeContents: Array<TreeNodeInfo<Article>> = _.map(
    props.data.articles,
    (article) => buildPartNode(article)
  );

  const [nodes, dispatch] = React.useReducer(treeExampleReducer, [
    ...treeContents,
  ]);

  const handleNodeClick = React.useCallback(
    (
      node: TreeNodeInfo,
      nodePath: NodePath,
      e: React.MouseEvent<HTMLElement>
    ) => {
      const originallySelected = node.isSelected;
      if (!e.shiftKey) {
        dispatch({ type: "DESELECT_ALL" });
      }
      dispatch({
        payload: {
          path: nodePath,
          isSelected: originallySelected == null ? true : !originallySelected,
        },
        type: "SET_IS_SELECTED",
      });
      console.log("node", node);
      if (node.nodeData) {
        if (_.get(node.nodeData, "__typename") === "Article") {
          setSelectState({
            distributor: null,
            articleSource: null,
            article: node.nodeData! as any,
          });
        } else if (_.get(node.nodeData, "__typename") === "ArticleSource") {
          setSelectState({
            distributor: null,
            article: null,
            articleSource: node.nodeData! as any,
          });
        } else if (_.get(node.nodeData, "__typename") === "Distributor") {
          setSelectState({
            articleSource: null,
            article: null,
            distributor: node.nodeData! as any,
          });
        }
        if (node.isSelected) {
          setSelectState({
            articleSource: null,
            article: null,
            distributor: null,
          });
        }
      }
    },
    []
  );

  const handleNodeCollapse = React.useCallback(
    (_node: TreeNodeInfo, nodePath: NodePath) => {
      dispatch({
        payload: { path: nodePath, isExpanded: false },
        type: "SET_IS_EXPANDED",
      });
    },
    []
  );

  const handleNodeExpand = React.useCallback(
    (_node: TreeNodeInfo, nodePath: NodePath) => {
      dispatch({
        payload: { path: nodePath, isExpanded: true },
        type: "SET_IS_EXPANDED",
      });
    },
    []
  );

  let detailsNode = <></>;
  if (selectState.articleSource !== null) {
    detailsNode = <ArticleSourceDetail source={selectState.articleSource} />;
  } else if (selectState.article !== null) {
    detailsNode = <PartDetail part={selectState.article} />;
  } else if (selectState.distributor !== null) {
    detailsNode = <DistributorDetail distributor={selectState.distributor} />;
  } else {
    detailsNode = <></>;
  }

  const filteredNodes = nodes.filter(
    (n) =>
      searchQuery.length === 0 ||
      n.label.toString().toLowerCase().indexOf(searchQuery.toLowerCase()) >= 0
  );

  return (
    <Card elevation={3} style={{ padding: 0 }}>
      <FlexRowStretch
        style={{
          width: 1200,
          height: 600,
          padding: 0,
        }}
      >
        <FlexBox
          style={{
            borderRight: "1px solid gray",
            width: 350,
          }}
        >
          <InputGroup
            type="text"
            placeholder="search"
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            value={searchQuery}
          />
          <Tree
            contents={filteredNodes}
            onNodeClick={handleNodeClick}
            onNodeCollapse={handleNodeCollapse}
            onNodeExpand={handleNodeExpand}
            className={"filltree"}
          />
        </FlexBox>
        <FlexBox
          style={{
            width: "100%",
            padding: 20,
          }}
        >
          {detailsNode}
        </FlexBox>
      </FlexRowStretch>
    </Card>
  );
};

export const ArticleSourceListView = (props: {}) => {
  const [articleSourceViewState, setArticleSourceViewState] =
    useState<ArticleSourceListViewState>({ data: null });

  const listQuery = useQuery<ArticleSourceViewResponse>(
    ArticleSourceViewQuery,
    {
      onCompleted: (d) => setArticleSourceViewState({ data: d }),
      fetchPolicy: "no-cache",
    }
  );

  const refetch = () => {
    listQuery.refetch().then((result) => {
      setArticleSourceViewState({ data: result.data });
    });
  };

  if (articleSourceViewState.data === null) return <Spinner />;

  if (articleSourceViewState.data !== null) {
    return (
      <FlexContainer>
        <ArticleSourceTree
          data={articleSourceViewState.data}
          refetch={refetch}
        />
      </FlexContainer>
    );
  }

  return <p>Error {listQuery.error}</p>;
};
